import React, { useEffect } from "react";
import { Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useStaticQuery, graphql, Link } from "gatsby";
import { getSrc } from "gatsby-plugin-image";
import { isLoggedIn, logout } from "../../services/auth";
import { useRecoilState } from "recoil";
import { companyState } from "../states";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Header() {
  const data = useStaticQuery(graphql`
  query HeaderQuery {
    allStrapiResortWebsites {
      edges {
        node {
          company_address
          company_email
          company_name
          domain_name
          phone_number
          company_logo {
            url
          }
          pageImages {
            localFile {
              childImageSharp {
                gatsbyImageData(layout: FIXED)
              }
            }
          }
        }
      }
    }
    allStrapiLocations {
      edges {
        node {
          city
          state
          images {
            url
          }
        }
      }
    }
    allStrapiBlogs {
      edges {
        node {
          categories
          excerpt
          title
          slug
          featured_image {
            url
          }
        }
      }
    }
  }
  `);
  const [company, setCompany] = useRecoilState(companyState);
  const locations = data.allStrapiLocations.edges;
  const blogs = data.allStrapiBlogs.edges;
  const [loggedIn, setLoggedIn] = React.useState(false);
  useEffect(() => {
    setLoggedIn(isLoggedIn());
    setCompany(data.allStrapiResortWebsites.edges[0].node);
  }, []);

  return (
    <Popover className="relative bg-white z-50">
      <div className="flex items-center justify-between px-6 py-2 md:justify-start md:space-x-10">
        <div className="flex justify-start lg:w-0 lg:flex-1">
          <a href="/">
            <img
              className="h-24 w-auto sm:h-16 sm:w-full"
              src={company?.company_logo.url}
              alt="owners accommodation"
            />
          </a>
        </div>
        <div className="-my-2 -mr-2 md:hidden">
          <Popover.Button className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
            <span className="sr-only">Open menu</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </Popover.Button>
        </div>
        <Popover.Group as="nav" className="hidden space-x-10 md:flex">
          <Popover className="relative">
            {({ open }) => (
              <>
                <Popover.Button
                  className={classNames(
                    open ? "text-gray-900" : "text-gray-500",
                    "group inline-flex items-center rounded-md bg-white text-base font-medium hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  )}
                >
                  <span>Locations</span>
                  <ChevronDownIcon
                    className={classNames(
                      open ? "text-gray-600" : "text-gray-400",
                      "ml-2 h-5 w-5 group-hover:text-gray-500"
                    )}
                    aria-hidden="true"
                  />
                </Popover.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <Popover.Panel className="absolute z-10 -ml-4 mt-3 w-screen max-w-md transform lg:left-1/2 lg:ml-0 lg:max-w-2xl lg:-translate-x-1/2">
                    <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                      <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:px-8 sm:py-8 lg:grid-cols-2">
                        {locations.map((location, l) => (
                          <Link
                            key={l}
                            to={`/listings/?uiState[listings][query]=${encodeURIComponent(
                              location.node.city
                            )}&page=1`}

                            className="-m-3 flex items-start rounded-lg p-3 hover:bg-gray-50"
                          >

                            <div className="flex h-1/2 w-1/2 flex-shrink-0 items-center justify-center rounded-md bg-blue-500">
                              <img src={location.node.images[0]?.url} alt="" className="rounded-md"/>
                            </div>
                            <div className="ml-4">
                              <p className="text-base font-medium text-gray-900">
                                {location.node.city}
                              </p>
                              <p className="mt-1 text-sm text-gray-500">
                                {location.node.state}
                              </p>
                            </div>
                          </Link>
                        ))}
                      </div>
                      <div className="bg-gray-50 sm:p-4 text-center">
                        <div className="items-center">
                          <div className="ml-3 inline-flex items-center rounded-md bg-blue-100 px-3 py-2 text-lg font-medium leading-5 text-blue-800">
                            <Link to="/listings">
                              View all properties
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Popover.Panel>
                </Transition>
              </>
            )}
          </Popover>

          <a
            href="/about"
            className="text-base font-medium text-gray-500 hover:text-gray-900"
          >
            About
          </a>
          <a
            href="/contact"
            className="text-base font-medium text-gray-500 hover:text-gray-900"
          >
            Contact
          </a>

          <Popover className="relative z-50">
            {({ open }) => (
              <>
                <Popover.Button
                  className={classNames(
                    open ? "text-gray-900" : "text-gray-500",
                    "group inline-flex items-center rounded-md bg-white text-base font-medium hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  )}
                >
                  <span>Blogs</span>
                  <ChevronDownIcon
                    className={classNames(
                      open ? "text-gray-600" : "text-gray-400",
                      "ml-2 h-5 w-5 group-hover:text-gray-500"
                    )}
                    aria-hidden="true"
                  />
                </Popover.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <Popover.Panel className="absolute left-1/2 z-10 mt-3 w-screen max-w-xs -translate-x-1/2 transform px-2 sm:px-0">
                    <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                      <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
                        {blogs.map((blog, b) => (

                          <Link
                            key={b}
                            to={`/news/${blog.node.slug}`}
                            className="-m-3 block rounded-md p-3 hover:bg-gray-50"
                          >

                            <p className="text-base font-medium text-gray-900">
                              {blog.node.title}
                            </p>
                            <p className="mt-1 text-sm text-gray-500">
                              {blog.node.categories}
                            </p>
                          </Link>
                        ))}
                      </div>
                      <div className="bg-gray-50 sm:p-4 text-center">
                        <div className="items-center">
                          <div className="ml-3 inline-flex items-center rounded-md bg-blue-100 px-3 py-2 text-lg font-medium leading-5 text-blue-800">
                            <Link to="/news">
                              View all Blog Posts
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Popover.Panel>
                </Transition>
              </>
            )}
          </Popover>
        </Popover.Group>
        <div className="hidden items-center justify-end md:flex md:flex-1 lg:w-0">
          {!loggedIn ? (<><a
            href="/login"
            className="whitespace-nowrap text-base font-medium text-gray-500 hover:text-gray-900"
          >
            Sign in
          </a>
            <Link
              to="/register_form"
              className="ml-8 inline-flex items-center justify-center whitespace-nowrap rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700"
            >
              Sign up
            </Link></>) : (<>
              <a
                href="/login"
                className="whitespace-nowrap text-base font-medium text-gray-500 hover:text-gray-900"
                onClick={logout}
              >
                Sign out
              </a>
              <a
                href="/profile"
                className="ml-8 inline-flex items-center justify-center whitespace-nowrap rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700"
              >
                Profile
              </a>
            </>)}

        </div>
      </div>

      <Transition
        as={Fragment}
        enter="duration-200 ease-out"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="duration-100 ease-in"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Popover.Panel
          focus
          className="absolute inset-x-0 top-0 origin-top-right transform p-2 transition md:hidden"
        >
          <div className="divide-y-2 divide-gray-50 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
            <div className="px-5 pt-5 pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <img
                    className="h-8 w-auto"
                    src="https://tailwindui.com/img/logos/mark.svg?color=blue&shade=600"
                    alt="Your Company"
                  />
                </div>
                <div className="-mr-2">
                  <Popover.Button className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </Popover.Button>
                </div>
              </div>
              <div className="mt-6">
                <nav className="grid grid-cols-1 gap-7">
                  {locations.map((location, l) => (
                    <a
                      key={l}
                      href=""
                      className="-m-3 flex items-center rounded-lg p-3 hover:bg-gray-50"
                    >
                      <div className="flex h-1/4 w-1/4 flex-shrink-0 items-center justify-center rounded-md bg-blue-500 text-white">
                        <img src={location.node.images[0]?.url} alt="" className="rounded-md"/>
                      </div>
                      <div className="ml-4 text-base font-medium text-gray-900">
                        {location.node.city}
                      </div>
                    </a>
                  ))}
                </nav>
              </div>
            </div>
            <div className="py-6 px-5">
              <div className="grid grid-cols-2 gap-4">
                {blogs.map((blog, b) => (
                  <a
                    key={b}
                    href=""
                    className="text-base font-medium text-gray-900 hover:text-gray-700"
                  >
                    {blog.node.title}
                  </a>
                ))}
              </div>
              <div className="mt-6">
                <a
                  href="#"
                  className="flex w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700"
                >
                  Sign up
                </a>
                <p className="mt-6 text-center text-base font-medium text-gray-500">
                  Existing customer?{" "}
                  <Link to="/login" className="text-blue-600 hover:text-blue-500">
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  );
}
